// src/navigations/OpdsNav.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { useLanguageContext } from "./homenav";
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Settings,
  User,
  BarChart as BarChartIcon,
  LogOut,
  Menu,
  X,
  ChevronDown,
  HelpingHand,
  Wifi,
  WifiOff,
  Trash,
  GalleryHorizontal,
  Bell,
} from "lucide-react";
import useOnlineStatus from "../hooks/useOnlineStatus";
import { useNotifications } from "../contexts/NotificationContext";
import { useSelfRegisteredNotifications } from "../hooks/useSelfRegisteredNotifications";
import FloatingAccessibilityControls from "../components/FloatingAccessibilityControls";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

export default function OpdsNav() {
  const { t, language, changeLanguage } = useLanguageContext();
  const languageRef = useRef(null);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const currentUserId = currentUser?.id || null;
  const currentUserEmail = currentUser?.email || '';
  const [membersCount, setMembersCount] = useState(0);
  const [pendingMembersCount, setPendingMembersCount] = useState(0);
  const [rejectedMembersCount, setRejectedMembersCount] = useState(0);
  
  const navLinks = [
    { name: t('dashboard'), to: "/dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
    { name: t('members'), to: "/members", icon: <Users className="w-5 h-5" /> },
    { name: t('messages'), to: "/chats", icon: <MessageSquare className="w-5 h-5" /> },
    { name: t('analysisAndReport'), to: "/opdreports", icon: <BarChartIcon className="w-5 h-5" /> },
    { name: t('selfRegistered'), to: "/self-registered-members", icon: <Users className="w-5 h-5" /> },
    { name: t('trash'), to: "/deleted-members", icon: <Trash className="w-5 h-5" /> },
  ];
  
  const [isOpen, setIsOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [supportsOpen, setSupportsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [selfRegNewCount, setSelfRegNewCount] = useState(0);
  const [selfRegNewItems, setSelfRegNewItems] = useState([]);
  const [chatConversations, setChatConversations] = useState([]);
  const notifRef = useRef(null);

  // Prepare ordered nav segments to insert Members accordion above Messages
  const messagesIndex = navLinks.findIndex((l) => l.to === "/chats");
  const linksBeforeMessages = (messagesIndex >= 0 ? navLinks.slice(0, messagesIndex) : navLinks)
    .filter((l) => l.to !== "/members" && l.to !== "/opdreports");
  const messagesLink = navLinks.find((l) => l.to === "/chats");
  const linksAfterMessages = (messagesIndex >= 0 ? navLinks.slice(messagesIndex + 1) : [])
    .filter((l) => l.to !== "/opdreports");

  // Close language dropdown when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth >= 768) { // only for desktop
        if (languageRef.current && !languageRef.current.contains(event.target)) {
          setLanguageOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!notificationsOpen) return;
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [notificationsOpen]);
  const isOnline = useOnlineStatus();
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification } = useNotifications();

  // Filter notifications to only show self-registered member notifications for OPD users
  const opdNotifications = notifications.filter(notification => notification.type === 'self-registered');
  const opdUnreadCount = opdNotifications.filter(notification => !notification.read).length;

  // Self-registered members notifications hook
  const { fetchNewSelfRegisteredMembers } = useSelfRegisteredNotifications();

  const handleLogout = () => setLogoutOpen(true);
  const confirmLogout = () => {
    try {
      // Clear persisted auth/session keys
      localStorage.removeItem('currentUser');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userData');
      sessionStorage.clear();
    } catch (_) {}
    setLogoutOpen(false);
    navigate("/login");
  };

  // Fetch new self-registered members on OPD login
  useEffect(() => {
    let isMounted = true;

    const fetchNotifications = async () => {
      if (isMounted) {
        await fetchNewSelfRegisteredMembers();
      }
    };

    fetchNotifications();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Poll chats API to compute unread messages count for current user
  useEffect(() => {
    let timer;
    const fetchUnread = async () => {
      try {
        if (!currentUserId) { setUnreadMessageCount(0); return; }
        const res = await fetch(`https://aaapd.org.et/gallery_backend/api/get_conversations.php?userId=${currentUserId}`);
        const convs = await res.json();
        if (Array.isArray(convs)) {
          // Exclude locally dismissed conversations only if lastMessageAt <= dismissedAt
          const dismissedKey = currentUserId ? `chatDismissed:${currentUserId}` : null;
          let dismissed = {};
          if (dismissedKey) {
            try {
              const raw = JSON.parse(localStorage.getItem(dismissedKey) || '{}');
              dismissed = Array.isArray(raw) ? {} : (raw || {});
            } catch (_) { dismissed = {}; }
          }
          const total = convs
            .filter(c => {
              const dAt = dismissed?.[String(c.id)];
              if (!dAt) return true;
              const lastAt = c.lastMessageAt ? new Date(c.lastMessageAt) : null;
              return lastAt && lastAt > new Date(dAt);
            })
            .reduce((acc, c) => acc + (Number(c.unreadCount) || 0), 0);
          setUnreadMessageCount(total);
          setChatConversations(convs);
        } else {
          setUnreadMessageCount(0);
          setChatConversations([]);
        }
      } catch (_) {
        setUnreadMessageCount(0);
        setChatConversations([]);
      }
    };
    fetchUnread();
    timer = setInterval(fetchUnread, 10000);
    return () => { if (timer) clearInterval(timer); };
  }, [currentUserId]);

  // Poll self-registered members to compute new items since last seen for this OPD email
  useEffect(() => {
    let timer;
    const storageKey = currentUserEmail ? `selfRegLastSeen:${currentUserEmail}` : null;
    const ensureInitialLastSeen = () => {
      if (!storageKey) return;
      const existing = localStorage.getItem(storageKey);
      if (!existing) {
        localStorage.setItem(storageKey, new Date().toISOString());
      }
    };
    const fetchSelfReg = async () => {
      try {
        if (!currentUserEmail) { setSelfRegNewCount(0); setSelfRegNewItems([]); return; }
        ensureInitialLastSeen();
        const lastSeenIso = storageKey ? localStorage.getItem(storageKey) : null;
        const res = await fetch(`https://aaapd.org.et/gallery_backend/api/getSelfRegisteredMembers.php?email=${encodeURIComponent(currentUserEmail)}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        let newItems = list;
        if (lastSeenIso) {
          const lastSeen = new Date(lastSeenIso);
          newItems = list.filter(it => it?.created_at && new Date(it.created_at) > lastSeen);
        }
        // Filter out locally dismissed self-registered items
        const dismissedKey = currentUserEmail ? `selfRegDismissed:${currentUserEmail}` : null;
        let dismissed = [];
        if (dismissedKey) {
          try { dismissed = JSON.parse(localStorage.getItem(dismissedKey) || '[]'); } catch (_) { dismissed = []; }
        }
        if (Array.isArray(dismissed) && dismissed.length > 0) {
          const dismissedSet = new Set(dismissed.map(id => Number(id)));
          newItems = newItems.filter(it => !dismissedSet.has(Number(it.id)));
        }
        // Sort desc by created_at
        newItems.sort((a,b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setSelfRegNewCount(newItems.length);
        setSelfRegNewItems(newItems.slice(0, 10));
      } catch (_) {
        setSelfRegNewCount(0);
        setSelfRegNewItems([]);
      }
    };
    fetchSelfReg();
    timer = setInterval(fetchSelfReg, 10000);
    return () => { if (timer) clearInterval(timer); };
  }, [currentUserEmail]);

  useEffect(() => {
    let cancelled = false;

    const fetchMembersCount = async () => {
      if (cancelled) return;
      try {
        if (!currentUserEmail) {
          setMembersCount(0);
          setPendingMembersCount(0);
          setRejectedMembersCount(0);
          return;
        }

        // Get OPD profile to determine sub city (same pattern as MembersPage)
        let subCity = '';
        try {
          const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
          const profData = await profRes.json();
          if (profData?.status === 'success' && profData?.data?.sub_city) {
            subCity = profData.data.sub_city;
          }
        } catch (_) {
          subCity = '';
        }

        const res = await fetch(`${API_BASE_URL}/getAllMembers.php`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        const norm = (s) => (s || '').toString().trim().toLowerCase();
        const subCityNorm = norm(subCity);

        const approvedForOpd = list.filter(m =>
          (!subCity || norm(m.subCity) === subCityNorm) && norm(m.aaapd_status) === 'approved'
        );
        const pendingForOpd = list.filter(m =>
          (!subCity || norm(m.subCity) === subCityNorm) && norm(m.aaapd_status) === 'pending'
        );
        const rejectedForOpd = list.filter(m =>
          (!subCity || norm(m.subCity) === subCityNorm) && norm(m.aaapd_status) === 'rejected'
        );

        if (cancelled) return;
        setMembersCount(approvedForOpd.length);
        setPendingMembersCount(pendingForOpd.length);
        setRejectedMembersCount(rejectedForOpd.length);
      } catch (_) {
        if (cancelled) return;
        setMembersCount(0);
        setPendingMembersCount(0);
        setRejectedMembersCount(0);
      }
    };

    fetchMembersCount();
    const intervalId = setInterval(fetchMembersCount, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [currentUserEmail]);

  const NavLinkItem = ({ to, icon, label, badge, exact = false }) => (
    <NavLink
      to={to}
      end={exact}
      onClick={() => setIsOpen(false)}
      className={({ isActive }) =>
        `flex items-center justify-between gap-3 px-4 py-2 rounded-lg transition-colors ${
          isActive
            ? "bg-teal-100 text-teal-700 font-semibold"
            : "text-gray-700 hover:bg-gray-100"
        }`
      }
    >
      <div className="flex items-center gap-3">
        {icon}
        <span>{label}</span>
      </div>
      {badge != null && badge !== undefined && (
        <span className="text-sm bg-teal-100 text-teal-700 px-2 rounded-full">
          {badge}
        </span>
      )}
    </NavLink>
  );

  const isGroup = (conv) => (Array.isArray(conv?.participants) && conv.participants.length > 2) || ((conv?.name || '').toLowerCase().includes('group'));
  const getOtherMember = (conv) => {
    if (!conv || !Array.isArray(conv.participants)) return { id: null, name: '' };
    const other = conv.participants.find(p => Number(p.id) !== Number(currentUserId));
    return other || { id: null, name: conv.name || '' };
  };
  const getConvDisplayName = (conv) => isGroup(conv) ? (conv?.name || 'Group') : (getOtherMember(conv).name || 'Conversation');

  const AccordionButton = ({ open, setOpen, icon, label }) => (
    <button
      onClick={() => setOpen(!open)}
      className="w-full flex items-center justify-between gap-3 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 font-medium transition-colors"
    >
      <div className="flex items-center gap-3">
        {icon}
        <span>{label}</span>
      </div>
      <ChevronDown
        className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
      />
    </button>
  );

  return (
    <>
      {/* Hamburger Menu for Mobile */}
      <button
        className="md:hidden fixed top-4 left-4 z-[60] text-gray-800 bg-white/50 backdrop-blur-sm p-2 rounded-full shadow-md"
        onClick={() => setIsOpen(true)}
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Overlay for Mobile */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-[60]"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-[70] flex flex-col bg-white shadow-lg transition-transform duration-300 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-64 w-72`}
      >
        {/* Close button for mobile */}
        <button
          className="md:hidden absolute top-4 right-4 text-gray-600 hover:text-gray-900"
          onClick={() => setIsOpen(false)}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Logos and Title */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src="/aaapdmainlogo.jpg" alt="ZOA Logo" className="h-20 w-auto" />
            
          </div>

            {/* Online Status Indicator */}
            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2">
                {isOnline ? (
                  <>
                    <div className="relative">
                      <Wifi className="w-4 h-4 text-green-600" />
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                    <span className="text-sm font-medium text-green-700">{t('onlineStatus')}</span>
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <WifiOff className="w-4 h-4 text-red-600" />
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    </div>
                    <span className="text-sm font-medium text-red-700">{t('offlineStatus')}</span>
                  </>
                )}
              </div>

              {/* Bell Notification Icon */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => {
                    const open = !notificationsOpen;
                    setNotificationsOpen(open);
                    if (open && currentUserEmail) {
                      const storageKey = `selfRegLastSeen:${currentUserEmail}`;
                      localStorage.setItem(storageKey, new Date().toISOString());
                      // reset count on open; it will refresh on next poll
                      setSelfRegNewCount(0);
                    }
                  }}
                  className="relative p-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Bell className="w-5 h-5 text-gray-600" />
                  {/* Notification Badge */}
                  {unreadMessageCount + selfRegNewCount > 0 && (
                    <div className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-[10px] leading-none text-white font-bold">{(unreadMessageCount + selfRegNewCount) > 99 ? '99+' : (unreadMessageCount + selfRegNewCount)}</span>
                    </div>
                  )}
                </button>

                {/* Notifications Dropdown - show unread chat messages and new self-registered members */}
                {notificationsOpen && (
                  <div className="absolute left-full top-0 ml-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-800">{t('notifications')}</h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {/* Unread chat messages section */}
                      <div>
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">{t('messages')}</div>
                        {chatConversations.filter(c => (Number(c.unreadCount) || 0) > 0).length === 0 ? (
                          <div className="px-4 pb-2 text-gray-400 text-sm">{t('noNewNotifications')}</div>
                        ) : (
                          chatConversations
                            .filter(c => (Number(c.unreadCount) || 0) > 0)
                            .filter(c => {
                              const dismissedKey = currentUserId ? `chatDismissed:${currentUserId}` : null;
                              if (!dismissedKey) return true;
                              let dismissed = {};
                              try {
                                const raw = JSON.parse(localStorage.getItem(dismissedKey) || '{}');
                                dismissed = Array.isArray(raw) ? {} : (raw || {});
                              } catch (_) { dismissed = {}; }
                              const dAt = dismissed?.[String(c.id)];
                              if (!dAt) return true;
                              const lastAt = c.lastMessageAt ? new Date(c.lastMessageAt) : null;
                              return lastAt && lastAt > new Date(dAt);
                            })
                            .sort((a,b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt))
                            .slice(0, 5)
                            .map(conv => (
                              <div key={conv.id} className="p-3 hover:bg-gray-50 border-b border-gray-100">
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold flex-shrink-0">
                                    {getConvDisplayName(conv).charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="text-sm font-semibold text-gray-800 truncate">{getConvDisplayName(conv)}</p>
                                      <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{Number(conv.unreadCount) || 0}</span>
                                    </div>
                                    <p className="text-xs text-gray-600 truncate">{conv.lastMessageText || 'New messages'}</p>
                                    <p className="text-[10px] text-gray-400 mt-1">{conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleString() : ''}</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // Locally dismiss with timestamp (do not mark as read on server)
                                      const dismissedKey = currentUserId ? `chatDismissed:${currentUserId}` : null;
                                      if (dismissedKey) {
                                        let dismissed = {};
                                        try {
                                          const raw = JSON.parse(localStorage.getItem(dismissedKey) || '{}');
                                          dismissed = Array.isArray(raw) ? {} : (raw || {});
                                        } catch (_) { dismissed = {}; }
                                        dismissed[String(conv.id)] = new Date().toISOString();
                                        localStorage.setItem(dismissedKey, JSON.stringify(dismissed));
                                      }
                                      // Update unread count locally by subtracting this convo's unread
                                      const dismissedUnread = Number(conv.unreadCount) || 0;
                                      setUnreadMessageCount(prev => Math.max(0, prev - dismissedUnread));
                                      // Force re-render
                                      setChatConversations(prev => [...prev]);
                                    }}
                                    className="text-gray-400 hover:text-gray-600 p-1"
                                    title="Dismiss"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                  </button>
                                </div>
                              </div>
                            ))
                        )}
                      </div>

                      {/* New self-registered members section */}
                      <div>
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Self-Registered Members</div>
                        {selfRegNewItems.length === 0 ? (
                          <div className="px-4 pb-2 text-gray-400 text-sm">{t('noNewNotifications')}</div>
                        ) : (
                          selfRegNewItems.map((m) => (
                            <div key={m.id} className="p-3 hover:bg-gray-50 border-b border-gray-100">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold flex-shrink-0">
                                  {(m.fullName || 'M').charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-800 truncate">{m.fullName || 'New Member'}</p>
                                  <p className="text-xs text-gray-600 truncate">{m.disabilityType || ''}</p>
                                  <p className="text-[10px] text-gray-400 mt-1">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const dismissedKey = currentUserEmail ? `selfRegDismissed:${currentUserEmail}` : null;
                                    if (dismissedKey) {
                                      let dismissed = [];
                                      try { dismissed = JSON.parse(localStorage.getItem(dismissedKey) || '[]'); } catch (_) { dismissed = []; }
                                      if (!dismissed.includes(m.id)) {
                                        dismissed.push(m.id);
                                        localStorage.setItem(dismissedKey, JSON.stringify(dismissed));
                                      }
                                    }
                                    const remaining = selfRegNewItems.filter(x => x.id !== m.id);
                                    setSelfRegNewItems(remaining);
                                    setSelfRegNewCount(Math.max(0, selfRegNewCount - 1));
                                  }}
                                  className="text-gray-400 hover:text-gray-600 p-1"
                                  title="Dismiss"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="p-3 border-t border-gray-200 flex items-center justify-between">
                      <button
                        onClick={async () => {
                          try {
                            // Mark all unread chat conversations as read for current user
                            const unreadConvs = chatConversations.filter(c => (Number(c.unreadCount) || 0) > 0);
                            await Promise.all(
                              unreadConvs.map(c => fetch('https://aaapd.org.et/gallery_backend/api/mark_read.php', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ conversationId: c.id, userId: currentUserId })
                              }).catch(() => null))
                            );
                            // Clear self-registered new items by updating last seen
                            if (currentUserEmail) {
                              const storageKey = `selfRegLastSeen:${currentUserEmail}`;
                              localStorage.setItem(storageKey, new Date().toISOString());
                            }
                            setSelfRegNewCount(0);
                            setSelfRegNewItems([]);
                            // Refresh conversations quickly
                            try {
                              if (currentUserId) {
                                const res = await fetch(`https://aaapd.org.et/gallery_backend/api/get_conversations.php?userId=${currentUserId}`);
                                const convs = await res.json();
                                if (Array.isArray(convs)) {
                                  setChatConversations(convs);
                                  const total = convs.reduce((acc, c) => acc + (Number(c.unreadCount) || 0), 0);
                                  setUnreadMessageCount(total);
                                } else {
                                  setChatConversations([]);
                                  setUnreadMessageCount(0);
                                }
                              }
                            } catch (_) {}
                          } catch (_) {
                            // ignore errors
                          }
                        }}
                        className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                      >
                        {t('markAllAsRead')}
                      </button>
                      <button onClick={() => { setNotificationsOpen(false); navigate('/chats'); }} className="text-sm text-blue-600 hover:text-blue-800 font-medium">{t('viewAll')}</button>
                    </div>
                  </div>
                )}
              </div>

              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
            </div>
        </div>

        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {linksBeforeMessages.map((link) => (
            <NavLinkItem key={link.name} to={link.to} icon={link.icon} label={link.name} exact={link.to === "/dashboard"} />
          ))}

          {/* Members Section (placed above Messages) */}
          <AccordionButton open={membersOpen} setOpen={setMembersOpen} icon={<Users className="w-5 h-5" />} label={t('members')} />
          {membersOpen && (
            <div className="pl-8 flex flex-col gap-1">
              <NavLinkItem to="/members" label={t('allMembers')} icon={<Users className="w-4 h-4" />} badge={membersCount} exact />
              <NavLinkItem to="/members/pending" label={t('pendingMembers')} icon={<Users className="w-4 h-4" />} badge={pendingMembersCount} />
              <NavLinkItem to="/members/rejected" label={t('rejectedMembers')} icon={<Users className="w-4 h-4" />} badge={rejectedMembersCount} />
              <NavLinkItem to="/members/renewals" label={t('renewals')} icon={<Users className="w-4 h-4" />} />
            </div>
          )}

          {/* Messages link rendered after Members accordion to keep it below */}
          {messagesLink && (
            <NavLinkItem to={messagesLink.to} icon={messagesLink.icon} label={messagesLink.name} />
          )}

          {/* Analysis & Reports Dropdown */}
          <AccordionButton open={reportsOpen} setOpen={setReportsOpen} icon={<BarChartIcon className="w-5 h-5" />} label={t('analysisAndReport')} />
          {reportsOpen && (
            <div className="pl-8 flex flex-col gap-1">
              <NavLinkItem to="/opdreports" label={t('analysisAndReport')} icon={<BarChartIcon className="w-4 h-4" />} />
              <NavLinkItem to="/opdplansreports" label={t('plansAndReports')} icon={<BarChartIcon className="w-4 h-4" />} />
            </div>
          )}

          {/* Supports Dropdown */}
          <AccordionButton open={supportsOpen} setOpen={setSupportsOpen} icon={<HelpingHand className="w-5 h-5" />} label={t('supportTracking')} />
          {supportsOpen && (
            <div className="pl-8 flex flex-col gap-1">
              <NavLinkItem to="/opdsupporttracking" label={t('supportTracking')} icon={<User className="w-4 h-4" />} />
              <NavLinkItem to="/opdsupport" label={t('supportReceived')} icon={<User className="w-4 h-4" />} />
            </div>
          )}

          {/* Management Dropdown */}
          <AccordionButton open={galleryOpen} setOpen={setGalleryOpen} icon={<GalleryHorizontal className="w-5 h-5" />} label={t('management')} />
          {galleryOpen && (
            <div className="pl-8 flex flex-col gap-1">
              <NavLinkItem to="/submit-gallery" label={t('galleryManagement')} icon={<User className="w-4 h-4" />} />
              <NavLinkItem to="/submit-news" label={t('blogManagement')} icon={<User className="w-4 h-4" />} />
            </div>
          )}

          {/* Remaining links after Messages (e.g. Self Registered, Trash) */}
          {linksAfterMessages.map((link) => (
            <NavLinkItem key={link.name} to={link.to} icon={link.icon} label={link.name} />
          ))}

          {/* Settings Dropdown */}
          <AccordionButton open={settingsOpen} setOpen={setSettingsOpen} icon={<Settings className="w-5 h-5" />} label={t('settings')} />
          {settingsOpen && (
            <div className="pl-8 flex flex-col gap-1">
              <NavLinkItem to="/opdprofile" label={t('profile')} icon={<User className="w-4 h-4" />} />
            </div>
          )}
          
        </nav>
        

        {/* Footer with Logout */}
        <div className="px-2 py-4 border-t border-gray-200 space-y-2">
            

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('logout')}</span>
            </button>
        </div>
      </div>

      {/* Logout Modal */}
      <Dialog.Root open={logoutOpen} onOpenChange={setLogoutOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[1200]" />
          <Dialog.Content className="fixed z-[1201] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[90vw] max-w-md">
            <Dialog.Title className="text-lg font-bold mb-2">
              {t('logoutConfirmationTitle')}
            </Dialog.Title>
            <Dialog.Description className="mb-4">
              {t('logoutConfirmationMessage')}
            </Dialog.Description>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">{t('cancel')}</button>
              </Dialog.Close>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                {t('yesLogout')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      {/* Floating Language Dropdown - Top Right Corner */}
<div className="fixed top-4 z-[80]" ref={languageRef} style={{ right: '96px' }}>
  <button
    onClick={() => setLanguageOpen(!languageOpen)}
    className="flex items-center px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brown-500 transition duration-300"
  >
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
    </svg>
    {language === 'en' ? 'Eng' : 'አማ'}
    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </button>

  {/* Language Dropdown Menu */}
  <div
    className={`absolute left-0 top-full mt-2 w-24 bg-white border border-gray-200 rounded-md shadow-lg z-50 transition-all duration-300 transform -translate-x-1/2 ${
      languageOpen ? "opacity-100 visible" : "opacity-0 invisible"
    }`}
  >
    <div className="py-1">
      <button
        onClick={() => {
          changeLanguage('en');
          setLanguageOpen(false);
        }}
        className={`w-full text-left px-4 py-2 text-sm transition duration-300 ${
          language === 'en'
            ? 'bg-brown-50 text-brown-600 font-medium'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        Eng
      </button>
      <button
        onClick={() => {
          changeLanguage('am');
          setLanguageOpen(false);
        }}
        className={`w-full text-left px-4 py-2 text-sm transition duration-300 ${
          language === 'am'
            ? 'bg-brown-50 text-brown-600 font-medium'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
      >
        አማ
      </button>
    </div>
  </div>
</div>


      {/* Accessibility Controls - Top Right Corner */}
      <FloatingAccessibilityControls position="top-right" />
    </>
  );
}
